/* eslint-disable */
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Drawer } from '../developments/analytics/shared/Drawer';
import { supabase } from '../../supabase';
import styles from './ActivityDrawer.module.css';

/**
 * T-015: System-level Activity drawer. Replaces the pre-T-015
 * Originals-portal Activity tab. Accessible from any portal via the
 * AppHeader trigger icon or the 'A' keyboard shortcut.
 *
 * Reuses the existing Drawer primitive at
 * src/components/developments/analytics/shared/Drawer.js
 * (Convention #35 + DO NOT TOUCH line 1511 — no modifications). The Drawer's
 * built-in title/Close header is intentionally minimized (no title prop
 * passed). All custom header content (eyebrow / italic title / sub / scope
 * tabs) lives inside children for prototype fidelity.
 *
 * Props:
 *   open       — boolean, controls visibility
 *   onClose    — function, called on Drawer's built-in Close + ESC + scrim
 *   onOpenTimeline — function. Opens the full Timeline screen (the unified,
 *                    cross-source feed) behind the "Full History →" footer.
 *                    Pass null/undefined to hide the footer link entirely.
 *                    NO LONGER ADMIN-GATED: this used to point at Settings →
 *                    Activity, which is admin-only. Timeline is all-staff and
 *                    scopes its own rows server-side, so every user gets the
 *                    link — theirs is simply a narrower feed.
 */
export function ActivityDrawer({ open, onClose, onOpenTimeline }) {
  const [scope, setScope] = useState('mine');

  const { data, isLoading, error } = useQuery({
    queryKey: ['system', 'activity', 'feed', scope],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_activity_system_feed', {
        p_scope: scope,
        p_limit: 200,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    enabled: open,
    staleTime: 30_000,
  });

  const rows = data?.rows ?? [];
  const grouped = useMemo(() => groupByDay(rows), [rows]);

  return (
    <Drawer open={open} onClose={onClose} layer="modal">
      {/* Brief F.2 Phase 2 — .activityRoot wrapper scopes New DNA tokens
          to the drawer's inner content (Convention #68). The shared Drawer
          renders via portal to document.body, so .catalogRoot tokens from
          App.js don't propagate; tokens are re-declared on .activityRoot. */}
      <div className={styles.activityRoot}>
        <header className={styles.head}>
          <div className={styles.eyebrow}>SYSTEM</div>
          <h2 className={styles.title}>Activity</h2>
          <div className={styles.sub}>Most recent edits across all portals</div>
          <div className={styles.scope} role="tablist" aria-label="Activity scope">
            <ScopeBtn active={scope === 'mine'} onClick={() => setScope('mine')}>
              Mine
            </ScopeBtn>
            <ScopeBtn active={scope === 'all'} onClick={() => setScope('all')}>
              All
            </ScopeBtn>
          </div>
        </header>

        <div className={styles.body}>
          {isLoading && <div className={styles.state}>Loading…</div>}
          {error && <div className={styles.state}>Could not load activity.</div>}
          {!isLoading && !error && rows.length === 0 && <EmptyState scope={scope} />}
          {!isLoading && !error && rows.length > 0 && grouped.map(({ day, items }) => (
            <section key={day}>
              <div className={styles.dayLabel}>{day}</div>
              {items.map(item => <FeedRow key={item.id} item={item} />)}
            </section>
          ))}
        </div>

        {onOpenTimeline && (
          <footer className={styles.foot}>
            <button
              type="button"
              className={styles.footLink}
              data-test="activity-full-history"
              onClick={() => { onClose(); onOpenTimeline(); }}
            >
              Full History →
            </button>
          </footer>
        )}
      </div>
    </Drawer>
  );
}

function ScopeBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={active ? `${styles.scopeBtn} ${styles.scopeBtnActive}` : styles.scopeBtn}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function FeedRow({ item }) {
  const initials = (item.user_email || '?')[0].toUpperCase();
  const time = formatRelativeTime(item.created_at);
  const description = formatAction(item.action, item.details);
  return (
    <div className={styles.feedRow} data-id={item.id}>
      <div className={styles.feedAvatarCol}>
        <div className={styles.feedAvatar}>{initials}</div>
        <div className={styles.feedConnector} />
      </div>
      <div className={styles.feedContent}>
        <div className={styles.feedActor}>{item.user_email || 'Unknown'}</div>
        <div className={styles.feedAction}>{description}</div>
        <div className={styles.feedTime}>{time}</div>
      </div>
    </div>
  );
}

function EmptyState({ scope }) {
  return (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
          <path d="M12 8v4l3 3" />
          <circle cx="12" cy="12" r="9" />
        </svg>
      </div>
      <div className={styles.emptyTitle}>
        {scope === 'mine' ? 'NO ACTIVITY ON YOUR ITEMS' : 'NO ACTIVITY YET'}
      </div>
      <div className={styles.emptyBody}>
        {scope === 'mine'
          ? 'When someone edits a dev you\'re scoped to, or you take an action yourself, it\'ll show up here. Switch to "All" to see everything happening across CLB.'
          : 'Actions appear here after edits.'}
      </div>
    </div>
  );
}

// === helpers ===

function groupByDay(rows) {
  // Buckets: TODAY / YESTERDAY / EARLIER THIS WEEK / EARLIER
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 86400_000);
  const weekStart = new Date(todayStart.getTime() - 6 * 86400_000);
  const buckets = { TODAY: [], YESTERDAY: [], 'EARLIER THIS WEEK': [], EARLIER: [] };
  for (const r of rows) {
    const d = new Date(r.created_at);
    // Bad/empty timestamp → predictable placement (EARLIER). Previously fell
    // here implicitly via NaN comparisons; made explicit so intent is clear.
    if (Number.isNaN(d.getTime())) buckets.EARLIER.push(r);
    else if (d >= todayStart) buckets.TODAY.push(r);
    else if (d >= yesterdayStart) buckets.YESTERDAY.push(r);
    else if (d >= weekStart) buckets['EARLIER THIS WEEK'].push(r);
    else buckets.EARLIER.push(r);
  }
  return Object.entries(buckets)
    .filter(([_, items]) => items.length > 0)
    .map(([day, items]) => ({ day, items }));
}

function formatRelativeTime(iso) {
  const d = new Date(iso);
  // Guard: an empty / unparseable created_at yields an Invalid Date, and every
  // numeric branch below is NaN-false → execution would fall through to
  // `d.toISOString()` which THROWS RangeError on an Invalid Date and (with no
  // boundary around the drawer) white-screened the whole app on the "All" feed.
  // Return a stable em-dash before any .getTime()/.toISOString() math.
  if (!iso || Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  if (diffD === 1) return `Yesterday ${hh}:${mm}`;
  if (diffD < 7) return `${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()]} ${hh}:${mm}`;
  return d.toISOString().slice(0, 10);
}

// v1 fallback: action verb + 1-3 identifying fields from details. Future
// action-type-specific renderers (e.g. "moved DA2228 from Hand Made to Color
// Sample") are out of scope for T-015.
function formatAction(action, details) {
  if (!details) return action || '';
  if (typeof details !== 'object') return `${action || 'event'} — ${String(details)}`;
  const interesting = ['dev_number', 'sku', 'designer', 'brand', 'stage', 'status', 'qc_report_id', 'text', 'item_sku'];
  const bits = [];
  for (const k of interesting) {
    if (details[k]) bits.push(`${k}: ${details[k]}`);
    if (bits.length >= 3) break;
  }
  return `${action || 'event'} — ${bits.join(' · ') || 'no details'}`;
}
